/**
 * Role-Based Home Component
 * Author: Iswe Solutions Team
 * 
 * Displays different home pages based on user role:
 * - Admin: Operations Overview (system monitoring dashboard)
 * - Non-admin: IntelligentHome (AI-powered surveillance insights)
 */

import React, { Suspense, useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';

// Lazy load both dashboards
// Non-admins land on the AI intelligence home (the /ai/home landing page),
// not the older SurveillanceIntelligencePage.
const ExecutiveOverviewPage = React.lazy(() => import('../pages/ExecutiveOverviewPage'));
const EnhancedDashboard = React.lazy(() => import('./EnhancedDashboard'));

const LoadingFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', background: 'transparent' }}>
    <CircularProgress sx={{ color: '#0d5c2e' }} />
  </Box>
);

// Get user from localStorage directly (more reliable than useGetIdentity for custom fields)
const getUserFromStorage = (): { role?: string; is_superuser?: boolean } | null => {
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      return JSON.parse(userStr);
    }
  } catch (e) {
    console.error('[RoleBasedHome] Error parsing user from localStorage:', e);
  }
  return null;
};

const RoleBasedHome: React.FC = () => {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  
  useEffect(() => {
    const user = getUserFromStorage();
    const role = user?.role?.toLowerCase();
    const adminCheck = role === 'admin' || role === 'superadmin' || user?.is_superuser === true;
    
    // Debug info
    // Debug removed for production
    
    setIsAdmin(adminCheck);
  }, []);
  
  // Still loading
  if (isAdmin === null) {
    return <LoadingFallback />;
  }
  
  // Admin users see Operations Overview; everyone else lands on the AI
  // intelligence home (same page as /ai/home).
  if (isAdmin) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <EnhancedDashboard />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <ExecutiveOverviewPage />
    </Suspense>
  );
};

export default RoleBasedHome;
